import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    buildCredentialUsageSelectionKey,
} from '@/lib/secrets/credentialUsageSelection';
import type {
    CredentialUsageListItem,
    CredentialUsageReference,
} from '@anchorbase/pillar-sdk';

export interface CredentialDisconnectDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    usages: ReadonlyArray<CredentialUsageListItem>;
    isLoadingUsages: boolean;
    usageLoadError: unknown;
    selectedUsageKeys: ReadonlyArray<string>;
    onToggleUsageSelection: (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ) => void;
    onConfirm: () => void;
    isDisconnecting: boolean;
}

export function CredentialDisconnectDialog({
    open,
    onOpenChange,
    usages,
    isLoadingUsages,
    usageLoadError,
    selectedUsageKeys,
    onToggleUsageSelection,
    onConfirm,
    isDisconnecting,
}: CredentialDisconnectDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>Disconnect Credential</DialogTitle>
                    <DialogDescription>
                        Select one or more usages to disconnect from this
                        credential.
                    </DialogDescription>
                </DialogHeader>

                {isLoadingUsages ? (
                    <p className="text-sm text-muted-foreground">
                        Loading credential usages...
                    </p>
                ) : usageLoadError ? (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {usageLoadError instanceof Error
                                ? usageLoadError.message
                                : 'Failed to load credential usages'}
                        </AlertDescription>
                    </Alert>
                ) : usages.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        This credential is not currently linked to any usage.
                    </p>
                ) : (
                    <div className="space-y-3 max-h-72 overflow-auto pr-2">
                        {usages.map((usage) => {
                            const usageSelectionKey: string =
                                buildCredentialUsageSelectionKey(usage);
                            const isSelected: boolean =
                                selectedUsageKeys.includes(usageSelectionKey);
                            return (
                                <label
                                    key={usageSelectionKey}
                                    className="flex items-start gap-3 rounded border p-3 cursor-pointer"
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={(checked: boolean) =>
                                            onToggleUsageSelection(usage, checked)
                                        }
                                    />
                                    <span className="text-sm">
                                        {usage.display_name}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={
                            selectedUsageKeys.length === 0 || isDisconnecting
                        }
                    >
                        {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
